import { createApiClient, apiClient } from "../../../api/apiClient";
import { LEGACY_TOUCHPOINT_VALUES, getTouchPointLegacyValue } from "../../../constants/touchpoints";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../store/utils/nhoErrorHandler';

// API response interface
interface ApiSurveyItem {
  Id: number;
  SectionType: "Header" | "Sub-Header" | "Data" | "Sub-Data" | "View";
  SectionValue: string;
  ParentId: number | null;
  Sort_Order: number;
  Section: number;
  Probation_30: number | null;
  Probation_60: number | null;
  Probation_90: number | null;
  Preferred_Method: number | null;
}

/**
 * Fetches survey items for a specific probation form type
 * @param observationType The type of observation ('30', '60', '90', 'preferred_method', 'Milestone 1', 'Milestone 2', 'Milestone 3')
 * @param probationState The state of the probation: 'new', 'in-progress', or 'completed'
 * @param recordId Optional probation record Id to send for in-progress records (so the API can return saved answers)
 * @returns A promise that resolves to an array of raw API survey items
 */
export const fetchTouchPointSurvey = async (
  observationType: string,
  probationState: 'new' | 'in-progress' | 'completed' = 'new',
  recordId?: number | null
): Promise<ApiSurveyItem[]> => {
  try {
    // Convert display names to legacy values if needed
    let apiObservationType = getTouchPointLegacyValue(observationType);
    
    // Transform observationType to match API parameter format
    let probFormParam = apiObservationType;
    if (apiObservationType === "preferred_method") {
      probFormParam = "Preferred_Method";
    }

    // Create API client instance
    const api = createApiClient();

    // new → org context from interceptor, no recordId
    // in-progress → org context from interceptor + recordId
    // completed → skip org context entirely
    const recordIdParam = probationState === 'in-progress' && recordId ? `&recordId=${recordId}` : '';
    const endpoint = `/new-hire-onboarding/probation?Prob_Form=${probFormParam}${recordIdParam}`;
    const config = probationState === 'completed' ? { _skipOrgContextForQuestions: true } : undefined;

    console.log('[NHO] Fetching probation questions - probationState:', probationState, 'recordId:', recordId);

    // Make API request using the apiClient
    const data = await apiClient(api, endpoint, "GET", undefined, config);

    // Filter and return items based on the observation type
    const filteredItems = data.filter((item: ApiSurveyItem) => {
      switch (apiObservationType) {
        case "30":
          return item.Probation_30 === 1;
        case "60":
          return item.Probation_60 === 1;
        case "90":
          return item.Probation_90 === 1;
        case "preferred_method":
          return item.Preferred_Method === 1;
        default:
          return false;
      }
    });

    return filteredItems;
  } catch (error) {
    console.error("Error fetching touch point survey:", error);
    try {
      handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_TOUCHPOINT_SURVEY, error, true, { observationType });
    } catch (e) {
      console.error('Error while dispatching NHO error handler:', e);
    }
    throw error;
  }
};

export type { ApiSurveyItem };

// API response interfaces for probation summary
interface ProbationData {
  id: number;
  probation_id: string;
  gpid: string;
  location: string;
  type_of_touchpoint: string;
  period_week: string;
  period: number;
  week: number | null;
  pepsi_week: number;
  emp_sign_status: number;
  mng_sign_status: number;
  created: string;
  created_by: string;
  coach_name: string | null;
  status: string;
  observations: string;
  additional_observations: string;
  employee_signature: string;
  manager_signature: string;
}

interface ProbationQuestionData {
  Data_Id: number;
  Response: number;
  Section: number;
  Created: string;
  Modified: string;
  Created_By: string;
  Employee_Name: string;
  Type_of_Touchpoint: string;
  Location_Name: string;
}

interface ProbationTypeData {
  probation: ProbationData;
  data: ProbationQuestionData[];
}

interface ProbationSummaryResponse {
  "Preferred Method": ProbationTypeData | null;
  "Milestone 1": ProbationTypeData | null;
  "Milestone 2": ProbationTypeData | null;
  "Milestone 3": ProbationTypeData | null;
  // Legacy API response keys for backward compatibility
  "Probation 30": ProbationTypeData | null;
  "Probation 60": ProbationTypeData | null;
  "Probation 90": ProbationTypeData | null;
  "Pulse Check": any; // Not needed for our use case
}

/**
 * Fetches existing probation data for a specific user and location
 * @param gpid The employee's GPID
 * @param location The location name
 * @returns A promise that resolves to probation summary data or null if no data exists
 */
export const fetchProbationSummary = async (
  gpid: string,
  location: string
): Promise<ProbationSummaryResponse | null> => {
  try {
    // Create API client instance
    const api = createApiClient();

    // API endpoint
    const endpoint = `/new-hire-onboarding/probation/summary/${gpid}?location=${encodeURIComponent(
      location
    )}`;

    // Make API request using the apiClient
    const data = await apiClient(api, endpoint, "GET");

    return data;
  } catch (error) {
    console.error("Error fetching probation summary:", error);
    try {
      handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_PROBATION_SUMMARY, error, true, { gpid, location });
    } catch (e) {
      console.error('Error while dispatching NHO error handler:', e);
    }
    // Return null if no data exists (404) or other errors
    return null;
  }
};

export type {
  ProbationSummaryResponse,
  ProbationTypeData,
  ProbationData,
  ProbationQuestionData,
};
